import React, {
  ChangeEvent,
  FC,
  HTMLAttributes,
  ReactNode,
  RefObject,
  useEffect,
  FocusEvent,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { LOAN_PURPOSE } from '~/actions/actionTypes';
import { IBaseComponent } from '../../../sharedInterfaces/IBaseComponent';
import { getDefaultLabelName } from '../../../services/DefaultLabelNameService';
import { IState } from '~/sharedInterfaces/IState';
import ComponentError from '../../ComponentError/ComponentError';
import { pushToGTM } from '~/actions/gtmActions';
import { getInputClassNames, hashCode } from '~/utils';
import { logUserFirstInteraction } from '~/actions/widgetContextActions';
import Tooltip from '../Tooltip/Tooltip';

export interface IInputProps extends IBaseComponent {
  value: string | boolean;
  label?: string;
  placeholder?: string;
  handleValidation?: () => void;
  children?: ReactNode;
  analyticsEvent: string;
  inputMode?: HTMLAttributes<HTMLInputElement>['inputMode'];
  autoCorrect?: string;
  autoCapitalize?: string;
  spellCheck?: boolean;
  tooltip?: boolean;
  disabled?: boolean;
  minLength?: number;
  maxLength?: number;
  className?: string;
  isValid?: boolean;
  onFocus?: (event: FocusEvent<any>) => void;
  innerRef?: RefObject<any>;
  autocomplete?: string;
}

const Input: FC<IInputProps> = ({
  componentName,
  label,
  type = 'text',
  value,
  onChange,
  handleValidation,
  onFocus,
  error,
  placeholder,
  children = null,
  analyticsEvent,
  inputMode,
  autoCapitalize,
  autoCorrect,
  spellCheck,
  tooltip,
  disabled,
  minLength,
  maxLength,
  className,
  isValid,
  autocomplete,
  innerRef,
}) => {
  const { locale, settings } = useSelector((state: IState) => state.widgetContext);
  const componentLabel = label || getDefaultLabelName(locale, componentName).label;
  const componentTooltipText = getDefaultLabelName(locale, componentName).tooltipText;
  const uniqueKey = hashCode(`${settings.id}${componentName}${componentLabel}`);
  const dispatch = useDispatch();

  const handlerOnBlur = (event: ChangeEvent<HTMLInputElement>) => {
    if (handleValidation) handleValidation();
    dispatch(
      pushToGTM({
        event: `${analyticsEvent}-change`,
        [analyticsEvent]: event.currentTarget.value,
        [`input-type-${analyticsEvent}`]: 'input',
      })
    );
  };

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (onChange) onChange(event);

    dispatch(logUserFirstInteraction());
  };

  useEffect(() => {
    if (handleValidation) handleValidation();
  }, []);

  useEffect(() => {
    if (error && type === 'text') {
      const inputValue = value
        ? 'q_user_a_input_field_format_error'
        : 'q_user_a_input_field_empty_error';
      dispatch(pushToGTM({ event: inputValue }));
    }
  }, [error]);

  return (
    <div
      className={getInputClassNames({
        type,
        tooltip,
        error,
        isValid,
        componentName,
        additionalClassName: className,
      })}
    >
      {/* Temporary solution before refactoring LoanPurpose component to have optional additional component */}
      {componentName === LOAN_PURPOSE ||
        componentName === 'homeAddress' ||
        (componentTooltipText && <Tooltip tooltip={tooltip} tooltipText={componentTooltipText} />)}
      <div className="qred-group">
        <div className="qred-row">
          <label htmlFor={uniqueKey}>
            <span className="qred-first">{componentLabel}</span>
          </label>
          <input
            name={uniqueKey}
            type={type}
            id={uniqueKey}
            value={value.toString()}
            placeholder={placeholder}
            onChange={handleOnChange}
            onBlur={handlerOnBlur}
            inputMode={inputMode}
            autoCorrect={autoCorrect}
            autoCapitalize={autoCapitalize}
            spellCheck={spellCheck}
            disabled={disabled}
            minLength={minLength}
            maxLength={maxLength}
            autoComplete={autocomplete}
            onFocus={onFocus}
            ref={innerRef}
          />
          {children}
          {error && <ComponentError error={error} />}
        </div>
      </div>
    </div>
  );
};

export default Input;
